import React from 'react'

import { Grid } from '../../components/Base/Base'
import { FlexBox } from '../../components/Base/FlexBox'
import { InfoTooltip, InfoTooltipParagraph } from '../../components/InfoTooltip/InfoTooltip'
import { Text } from '../../components/Text/Text'
import { Handler } from '../../helpers/typeHelper'
import { SafetyOverview, SafetyViolation } from '../../models/safety'
import { black, errorD1, kellyGreen, lightGray1, primaryBlack } from '../../theme/colors'
import { SafetyDashboardCardArrow, SafetyDashboardCardWithIcon } from './SafetyDashboard.styles'
import { SafetyDashboardNewViolationsLabel } from './SafetyDashboardNewViolationsLabel'

interface SafetyDashboardOosSummaryProps {
  onHeaderClick?: Handler
  safetyOverview: SafetyOverview
  violations: SafetyViolation[]
}

export function SafetyDashboardOosSummary({
  onHeaderClick,
  safetyOverview,
  violations,
}: SafetyDashboardOosSummaryProps) {
  const hasOosViolations = violations.some((violation) => violation.isOos)
  const hasNewViolations = violations.some((violation) => violation.isOos && violation.isNew)

  const vehicleNationalChange = safetyOverview.vehicleOss - safetyOverview.vehicleNationalAverage
  const vehicleNationalChangeText = Number(Math.abs(vehicleNationalChange).toFixed(2))
  const vehicleNationalChangeIsAbove = vehicleNationalChange > 0

  const driverNationalChange = safetyOverview.driverOss - safetyOverview.driverNationalAverage
  const driverNationalChangeText = Number(Math.abs(driverNationalChange).toFixed(2))
  const driverNationalChangeIsAbove = driverNationalChange > 0

  const hazmatNationalChange = safetyOverview.hazmatOss - safetyOverview.hazmatNationalAverage
  const hazmatNationalChangeText = Number(Math.abs(hazmatNationalChange).toFixed(2))
  const hazmatNationalChangeIsAbove = hazmatNationalChange > 0

  return (
    <>
      <Text variant="uppercaseLabel2" mb={1} fontColor={black}>
        Out of service rates
        <InfoTooltip>
          <InfoTooltipParagraph>
            Out-of-service (OOS) violations are critical safety violations that cause a driver or vehicle to
            be put out of service for a specified amount of time, or until the safety issues are fixed.
            Brokers and shippers are less likely to work with carriers that have high OOS rates, so the lower
            these rates are, the better.
          </InfoTooltipParagraph>
        </InfoTooltip>
      </Text>
      <SafetyDashboardCardWithIcon onClick={hasOosViolations ? onHeaderClick : undefined}>
        <Grid columns="1fr 1fr 1fr 1fr" xsColumns="1fr 1fr" gap="10px" xsGap="20px">
          <div>
            <Text variant="label2" fontColor={lightGray1}>
              Vehicle
            </Text>
            <Text variant="label1" fontColor={primaryBlack} fontWeight={600} mt={0.5}>
              {safetyOverview.vehicleOss}%
            </Text>
            {safetyOverview.vehicleOss ? (
              <Text variant="label4" fontColor={vehicleNationalChangeIsAbove ? errorD1 : kellyGreen} mt={0.5}>
                {vehicleNationalChangeText}% {vehicleNationalChangeIsAbove ? 'above' : 'below'} natl avg
              </Text>
            ) : null}
          </div>
          <div>
            <Text variant="label2" fontColor={lightGray1}>
              Driver
            </Text>
            <Text variant="label1" fontColor={primaryBlack} fontWeight={600} mt={0.5}>
              {safetyOverview.driverOss}%
            </Text>
            {safetyOverview.driverOss ? (
              <Text variant="label4" fontColor={driverNationalChangeIsAbove ? errorD1 : kellyGreen} mt={0.5}>
                {driverNationalChangeText}% {driverNationalChangeIsAbove ? 'above' : 'below'} natl avg
              </Text>
            ) : null}
          </div>
          <div>
            <Text variant="label2" fontColor={lightGray1}>
              Hazmat
            </Text>
            <Text variant="label1" fontColor={primaryBlack} fontWeight={600} mt={0.5}>
              {safetyOverview.hazmatOss}%
            </Text>
            {safetyOverview.hazmatOss ? (
              <Text variant="label4" fontColor={hazmatNationalChangeIsAbove ? errorD1 : kellyGreen} mt={0.5}>
                {hazmatNationalChangeText}% {hazmatNationalChangeIsAbove ? 'above' : 'below'} natl avg
              </Text>
            ) : null}
          </div>
          <div>
            {hasNewViolations && (
              <FlexBox
                alignItems="flex-start"
                xsAlignItems="flex-end"
                justifyContent="flex-end"
                styles="height: 100%"
              >
                <SafetyDashboardNewViolationsLabel />
              </FlexBox>
            )}
          </div>
        </Grid>

        {hasOosViolations ? <SafetyDashboardCardArrow /> : null}
      </SafetyDashboardCardWithIcon>
    </>
  )
}
